// PowerTranz client helpers. All credentials live server-side.
import { supabase } from "@/integrations/supabase/client";

export type PowerTranzCheckoutArgs = {
  amountCents: number;
  currency?: "USD" | "BSD" | "KYD" | "JMD" | "TTD" | "XCD" | "CAD" | "GBP" | "EUR";
  featureKey?: string;
  orderIdentifier?: string;
  returnUrl?: string;
};

export type PowerTranzCheckoutResult = {
  transaction_identifier: string;
  order_identifier: string;
  spi_token: string;
  redirect_html: string;
  return_url: string;
};

/**
 * Start a PowerTranz hosted-page payment.
 * Renders the returned `redirect_html` (an auto-submitting <form>) inside a
 * new tab so the cardholder lands on PowerTranz's PCI-compliant hosted page.
 */
export async function startPowerTranzCheckout(
  args: PowerTranzCheckoutArgs,
): Promise<PowerTranzCheckoutResult> {
  const { data, error } = await supabase.functions.invoke("powertranz-checkout", {
    body: {
      amount_cents: args.amountCents,
      currency: args.currency ?? "USD",
      feature_key: args.featureKey,
      order_identifier: args.orderIdentifier,
      return_url: args.returnUrl,
    },
  });
  if (error) throw error;
  const result = data as PowerTranzCheckoutResult;
  if (!result?.redirect_html) throw new Error("No PowerTranz redirect returned");

  const win = window.open("", "_blank");
  if (!win) throw new Error("Popup blocked — allow popups to complete payment");
  win.document.open();
  win.document.write(result.redirect_html);
  win.document.close();

  return result;
}

/** Look up a PowerTranz payment row by transaction identifier. */
export async function getPowerTranzPayment(transactionIdentifier: string) {
  const { data, error } = await supabase
    .from("powertranz_payments")
    .select("*")
    .eq("transaction_identifier", transactionIdentifier)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export type PowerTranzHoldState =
  | "pending" | "held" | "settled" | "failed" | "expired" | "refunded" | "voided" | "retrying";

export type PowerTranzPaymentRow = {
  id: string;
  transaction_identifier: string;
  order_identifier: string | null;
  amount_cents: number;
  currency: string;
  status: string;
  hold_state: PowerTranzHoldState;
  authorized_at: string | null;
  settled_at: string | null;
  last_polled_at: string | null;
  retry_count: number;
  next_retry_at: string | null;
  last_error: string | null;
  payment_type: string;
  feature_key: string | null;
  created_at: string;
};

/** Trigger a one-shot status poll for one or all pending payments. */
export async function pollPowerTranzStatus(
  args: { transactionIdentifier?: string; allPending?: boolean },
) {
  const { data, error } = await supabase.functions.invoke("powertranz-status", {
    body: {
      transaction_identifier: args.transactionIdentifier,
      all_pending: args.allPending,
    },
  });
  if (error) throw error;
  return data as { results: Array<Record<string, unknown>> };
}
